/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCurrentUserIsGuest, getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import TwoButtonModal from "@insite/content-library/Components/TwoButtonModal";
import DynamicDropdown, { OptionObject } from "@insite/mobius/DynamicDropdown";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { Checkbox, GridContainer, GridItem, LoadingOverlay, TextField } from "@insite/mobius/index";
import Tag from "@insite/mobius/Tag";
import { HasToasterContext, useToaster, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import addUserPetData from "@rc/App/Handlers/MyPets/addUserPet";
import getCatBreedsData from "@rc/App/Handlers/MyPets/getCatBreeds";
import getCatNeedsData from "@rc/App/Handlers/MyPets/getCatNeeds";
import getCatProductsData from "@rc/App/Handlers/MyPets/getCatProducts";
import getDogBreedsData from "@rc/App/Handlers/MyPets/getDogBreeds";
import getDogNeedsData from "@rc/App/Handlers/MyPets/getDogNeeds";
import getDogProductsData from "@rc/App/Handlers/MyPets/getDogProducts";
import getDogSizesData from "@rc/App/Handlers/MyPets/getDogSizes";
import getMyPetsData from "@rc/App/Handlers/MyPets/getMyPets";
import getPetData from "@rc/App/Handlers/MyPets/getPet";
import getPetBirthdayYearsData from "@rc/App/Handlers/MyPets/getPetBirthdayYears";
import getPetNumberOfVisitsData from "@rc/App/Handlers/MyPets/getPetNumberOfVisits";
import updatePetsBySubKeyData from "@rc/App/Handlers/MyPets/updatePetsBySubKey";
import { CustomAppState } from "@rc/App/Store/Reducers";
import {
    FilterValue,
    MiscellaneousModel,
    PetProduct,
    PetPropertyModel,
    PetsType,
    PetType,
} from "@rc/App/Store/Reducers/xMyPets";
import Button from "@rc/MobiusOverrides/Button/Button";
import Select from "@rc/MobiusOverrides/Select/Select";
import { xValidateOnSubmit } from "@rc/XDL/Utilities/xValidate/xFieldValidator";
import React from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    type: "cat" | "dog";
    mode: "add" | "edit";
    petKey?: string;
}

const mapStateToProps = (state: CustomAppState) => {
    const currentUserIsGuest = getCurrentUserIsGuest(state);
    const userName = state.context.session.userName;
    const session = getSession(state);

    const petNumberOfVists: MiscellaneousModel[] = state.xMyPets.petNumberOfVists;
    const years: [] = state.xMyPets.years;
    const catBreeds: MiscellaneousModel[] = state.xMyPets.catBreeds;
    const dogBreeds: MiscellaneousModel[] = state.xMyPets.dogBreeds;
    const catNeeds: MiscellaneousModel[] = state.xMyPets.catNeeds;
    const dogNeeds: MiscellaneousModel[] = state.xMyPets.dogNeeds;
    const catProducts: PetProduct[] = state.xMyPets.catProducts;
    const dogProducts: PetProduct[] = state.xMyPets.dogProducts;
    const dogSizes: FilterValue[] = state.xMyPets.dogSizes;

    return {
        isAuthenticated: userName && !currentUserIsGuest,
        isPcAdmin: session.userRoles.indexOf("PC_Admin") !== -1,
        petNumberOfVists,
        years,
        catBreeds,
        dogBreeds,
        catNeeds,
        dogNeeds,
        catProducts,
        dogProducts,
        dogSizes,
        subscriberKey: session?.properties?.subscriberKey || "",
        myPetsPageUrl: getPageLinkByPageType(state, "MyPetsPage")?.url,
    };
};

const mapDispatchToProps = {
    getMyPetsData,
    addUserPetData,
    getCatBreedsData,
    getCatNeedsData,
    getCatProductsData,
    getDogBreedsData,
    getDogNeedsData,
    getDogProductsData,
    getDogSizesData,
    getPetBirthdayYearsData,
    getPetNumberOfVisitsData,
    getPetData,
    updatePetsBySubKeyData,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface PetFormStyles {
    container?: InjectableCss;
    formContainer?: GridContainerProps;
    petNameGridItem?: GridItemProps;
    birthDayGridItem?: GridItemProps;
    bithdayContainer?: GridContainerProps;
    dateGridItem?: GridItemProps;
    breedGridItem?: GridItemProps;
    sizeInsteadOfBreedGridItem?: GridItemProps;
    petNumberOfVisitsGridItem?: GridItemProps;
    petSpecificNeedsGridItem?: GridItemProps;
    petHairLengthGridItem?: GridItemProps;
    feedingRCGridItem?: GridItemProps;
    productsRCGridItem?: GridItemProps;
    actionsGridItem?: GridItemProps;
}

export const petFormStyles: PetFormStyles = {
    formContainer: {
        gap: 20,
    },
    bithdayContainer: {
        gap: 20,
    },
    petNameGridItem: { width: 12 },
    birthDayGridItem: { width: 12 },
    dateGridItem: { width: 6 },
    breedGridItem: { width: 12 },
    sizeInsteadOfBreedGridItem: { width: 12 },
    petNumberOfVisitsGridItem: { width: 12 },
    petSpecificNeedsGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
            > div {
                width: 100%;

                &:nth-child(2) {
                    margin-top: 12px;
                    display: flex;
                    flex-wrap: wrap;
                }
            }
        `,
    },
    petHairLengthGridItem: { width: 12 },
    feedingRCGridItem: { width: 12 },
    productsRCGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
            > div {
                width: 100%;

                &:nth-child(2) {
                    margin-top: 12px;
                    display: flex;
                    flex-wrap: wrap;
                }
            }
        `,
    },
    actionsGridItem: { width: 12 },
};
// <XNG-Changes>
const formId = "petForm";
// </XNG-Changes>
const PetForm = ({
    isAuthenticated,
    isPcAdmin,
    type,
    mode,
    petKey,
    petNumberOfVists,
    years,
    catBreeds,
    dogBreeds,
    catNeeds,
    dogNeeds,
    catProducts,
    dogProducts,
    dogSizes,
    subscriberKey,
    addUserPetData,
    getCatBreedsData,
    getCatNeedsData,
    getCatProductsData,
    getDogBreedsData,
    getDogNeedsData,
    getDogProductsData,
    getDogSizesData,
    getPetBirthdayYearsData,
    getPetNumberOfVisitsData,
    getPetData,
    updatePetsBySubKeyData,
    history,
    myPetsPageUrl,
}: Props) => {
    const styles = petFormStyles;
    const dispatch = useDispatch();
    const toaster = useToaster();

    const months: Array<Object> = [
        {
            value: "01",
            display: "January",
        },
        {
            value: "02",
            display: "February",
        },
        {
            value: "03",
            display: "March",
        },
        {
            value: "04",
            display: "April",
        },
        {
            value: "05",
            display: "May",
        },
        {
            value: "06",
            display: "June",
        },
        {
            value: "07",
            display: "July",
        },
        {
            value: "08",
            display: "August",
        },
        {
            value: "09",
            display: "September",
        },
        {
            value: "10",
            display: "October",
        },
        {
            value: "11",
            display: "November",
        },
        {
            value: "12",
            display: "December",
        },
    ];

    const isfeedingRoyalCaninOptions: Array<Object> = [
        {
            value: "1",
            display: "Yes",
        },
        {
            value: "0",
            display: "No",
        },
    ];

    const petHairOptions: Array<Object> = [
        {
            value: "Long",
            display: "Long Hair",
        },
        {
            value: "Medium",
            display: "Medium Hair",
        },
        {
            value: "Short",
            display: "Short Hair",
        },
    ];

    const customMyPetsPetNameFieldRequiredMessage = siteMessage("Custom_MyPets_PetNameIsRequired") as string;
    const customMyPetsMonthFieldRequiredMessage = siteMessage("Custom_MyPets_MonthIsRequired") as string;
    const customMyPetsYearFieldRequiredMessage = siteMessage("Custom_MyPets_YearIsRequired") as string;
    const customMyPetsPetBreedFieldRequiredMessage = siteMessage("Custom_MyPets_BreedIsRequired") as string;
    const customMyPetsPetSizeFieldRequiredMessage = siteMessage("Custom_MyPets_PetSizeIsRequired") as string;
    const customMyPetsNumberOfVisitsFieldRequiredMessage = siteMessage(
        "Custom_MyPets_NumberOfVisitsIsRequired",
    ) as string;
    const customMyPetsFeedingFieldRequiredMessage = siteMessage("Custom_MyPets_FeedingIsRequired") as string;

    React.useEffect(() => {
        if (isAuthenticated) {
            if (!catBreeds?.length) {
                getCatBreedsData({});
            }
            if (!catNeeds?.length) {
                getCatNeedsData({});
            }
            if (!catProducts?.length) {
                getCatProductsData({});
            }
            if (!dogNeeds?.length) {
                getDogBreedsData({});
            }
            if (!dogNeeds?.length) {
                getDogNeedsData({});
            }
            if (!dogProducts?.length) {
                getDogProductsData({});
            }
            if (!dogSizes?.length) {
                getDogSizesData({});
            }
            if (!years?.length) {
                getPetBirthdayYearsData({});
            }
            if (!petNumberOfVists?.length) {
                getPetNumberOfVisitsData({});
            }
        }
    }, []);

    React.useEffect(() => {
        if (
            petKey &&
            catBreeds?.length &&
            catNeeds?.length &&
            dogBreeds?.length &&
            dogNeeds?.length &&
            catProducts?.length &&
            dogProducts?.length
        ) {
            setIsLoading(true);
            getPetData({
                subscriberKey,
                petKey,
                onSuccess: result => {
                    setPetData(result?.pet);
                    setIsLoading(false);
                },
                onError: () => {
                    toaster.addToast({
                        body: <Typography>{translate("Failed to Load Pet Information")}</Typography>,
                        messageType: "danger",
                    });
                    setIsLoading(false);
                },
            });
        }
    }, [catBreeds, catNeeds, dogBreeds, dogNeeds, catProducts, dogProducts]);

    React.useEffect(() => {
        if (!catProductOptions?.length) {
            if (catProducts?.length) {
                let options: OptionObject[] = [];
                options = options.concat(
                    catProducts.map((o: PetProduct) => ({ optionText: o.PageTitle, optionValue: o.Id })),
                );
                setCatProductOptions(options);
            }
        }
        if (!dogProductOptions?.length) {
            if (dogProducts?.length) {
                let options: OptionObject[] = [];
                options = options.concat(
                    dogProducts.map((o: PetProduct) => ({ optionText: o.PageTitle, optionValue: o.Id })),
                );
                setDogProductOptions(options);
            }
        }
    }, [catProducts, dogProducts]);

    const [isLoading, setIsLoading] = React.useState(false);
    const [showFormErrors, setShowFormErrors] = React.useState(false);
    const [petName, setPetName] = React.useState("");
    const [birthdayYear, setBirthdayYear] = React.useState("");
    const [birthdayMonth, setBirthdayMonth] = React.useState("");
    const [breed, setBreed] = React.useState("");
    const [petSize, setPetSize] = React.useState("");
    const [chooseBySize, setChooseBySize] = React.useState(false);
    const [numberOfVists, setNumberOfVists] = React.useState("");
    const [isFeedingRoyalCanin, setIsFeedingRoyalCanin] = React.useState("");
    const [showRemoveConfirmModal, setShowRemoveConfirmModal] = React.useState(false);
    const [petNeed, setPetNeed] = React.useState("");
    const [allNeedsSelected, setAllNeedsSelected] = React.useState([] as MiscellaneousModel[]);
    const [selectedProduct, setSelectedProduct] = React.useState("");
    const [catProductOptions, setCatProductOptions] = React.useState([] as OptionObject[]);
    const [dogProductOptions, setDogProductOptions] = React.useState([] as OptionObject[]);
    const [allProductsSelected, setAllProductsSelected] = React.useState([] as any[]);
    const [oldPetProperties, setOldPetProperties] = React.useState(undefined);
    const [petID, setPetID] = React.useState("");

    if (!isAuthenticated) {
        return null;
    }

    const petNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPetName(event.target.value);
    };

    const handleBirthdayYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const year = event.currentTarget.value;
        setBirthdayYear(year);
    };

    const handleBirthdayMonthChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const month = event.currentTarget.value;
        setBirthdayMonth(month);
    };

    const handleBreedChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const breed = event.currentTarget.value;
        setBreed(breed);
    };

    const handlePetSizeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const size = event.currentTarget.value;
        setPetSize(size);
    };

    const chooseBySizeEventHandler = (_: React.SyntheticEvent<Element, Event>, value: boolean) => {
        setChooseBySize(value);
    };

    const handleNumberOfVistsChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const numberOfVisits = event.currentTarget.value;
        setNumberOfVists(numberOfVisits);
    };

    const handleIsFeedingRoyalCaninChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const number = event.currentTarget.value;
        setIsFeedingRoyalCanin(number);
    };

    const handlePetNeedChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const option = event.currentTarget.value;
        const allNeeds = type.toLowerCase() === "cat" ? catNeeds : dogNeeds;
        const chosenNeed = allNeeds.filter((need: MiscellaneousModel) => need.Name === option);
        if (
            chosenNeed.length === 0 ||
            (chosenNeed.length > 0 && allNeedsSelected.find(e => e.Name === chosenNeed[0].Name))
        ) {
            return;
        }
        setPetNeed((chosenNeed[0] as MiscellaneousModel)?.Name);
        const selectedNeeds = [...allNeedsSelected, ...chosenNeed];
        setAllNeedsSelected(selectedNeeds);
    };

    const clickRemoveNeedHandler = (name: string) => {
        const selectedNeeds = [...allNeedsSelected];
        const result = selectedNeeds.filter(need => need.Name !== name);
        setAllNeedsSelected(result);
    };

    const listChangeHandler = (value?: string) => {
        setSelectedProduct(value || "");

        const allProducts = type.toLowerCase() === "cat" ? catProducts : dogProducts;
        const chosenProduct = allProducts.filter((product: PetProduct) => product.Id === value);
        if (
            chosenProduct.length === 0 ||
            (chosenProduct.length > 0 && allProductsSelected.find(e => e.Id === chosenProduct[0].Id))
        ) {
            return;
        }
        const selectedProducts = [...allProductsSelected, ...chosenProduct];
        setAllProductsSelected(selectedProducts as any);
    };

    const clickRemoveProductHandler = (id: string) => {
        const selectedProducts = [...allProductsSelected];
        const result = selectedProducts.filter((product: PetProduct) => product.Id !== id);
        setAllProductsSelected(result);
    };

    const setPetData = (pet: PetType) => {
        setOldPetProperties(pet.petProperties as any);
        setPetID(pet.petID);
        setPetName(pet.petName);
        if (pet.petBirthday) {
            const date = new Date(pet.petBirthday);
            const month = date.getMonth() + 1;

            setBirthdayMonth(month < 10 ? `0${month}` : `${month}`);
            setBirthdayYear(date.getFullYear().toString());
        }
        const isCat = pet.petSpecies?.toLowerCase() === "cat";
        const isDog = pet.petSpecies?.toLowerCase() === "dog";

        const allNeeds = isCat ? catNeeds : dogNeeds;
        const allProducts = isCat ? catProducts : dogProducts;
        let selectedNeeds = [] as MiscellaneousModel[];
        let selectedProducts = [] as any;

        let curProp: PetPropertyModel = {};
        for (let i = 0; i < pet.petProperties.length; i++) {
            curProp = pet.petProperties[i];

            if (curProp.ppName === "Product") {
                if (curProp.ppValue) {
                    const option = curProp.ppValue;
                    const chosenProduct = allProducts.filter((product: PetProduct) => product.Name === option);
                    selectedProducts = [...selectedProducts, ...chosenProduct];
                }
            } else if (curProp.ppName === "SpecificNeeds") {
                if (curProp.ppValue) {
                    const option = curProp.ppValue;
                    const chosenNeed = allNeeds.filter((need: MiscellaneousModel) => need.Name === option);
                    selectedNeeds = [...selectedNeeds, ...chosenNeed];
                }
            } else if (curProp.ppName === "PetSize") {
                setPetSize(curProp.ppValue || "");
            } else if (curProp.ppName === "CurrentlyFeedingRC") {
                setIsFeedingRoyalCanin(curProp.ppValue || "");
            } else if (curProp.ppName === "NumberOfVisits") {
                setNumberOfVists(curProp.ppValue || "");
            }
        }

        setAllProductsSelected(selectedProducts);
        setAllNeedsSelected(selectedNeeds);

        setBreed(pet.petBreed);
        if (isDog) {
            if (petSize) {
                setChooseBySize(true);
            } else {
                setChooseBySize(false);
            }
        }

        if (isCat) {
            if (petSize) {
                setChooseBySize(false);
            } else {
                setChooseBySize(true);
            }
        }
    };

    const formIsValid = () => {
        if (!petName || !birthdayMonth || !birthdayYear || !numberOfVists || !isFeedingRoyalCanin) {
            return false;
        }
        // this is a cat
        if (type?.toLowerCase() === "cat") {
            if (chooseBySize && !breed) {
                return false;
            }
            if (!chooseBySize && !petSize) {
                return false;
            }
            return true;
        }
        // this is a dog
        if (chooseBySize && !petSize) {
            return false;
        }
        if (!chooseBySize && !breed) {
            return false;
        }
        return true;
    };

    const handleFormSubmit = (event: React.MouseEvent<HTMLFormElement, MouseEvent>) => {
        event.preventDefault();
        prepareDataAndSubmit();
    };

    const prepareDataAndSubmit = (deletePet?: boolean) => {
        setIsLoading(true);
        const errors = xValidateOnSubmit(formId);

        if ((!formIsValid() && !deletePet) || errors.length > 0) {
            setIsLoading(false);
            setShowFormErrors(true);
            return;
        }

        const pet: PetType = {
            petID: "",
            SubscriberID: "",
            petName: "",
            petBirthday: "",
            petSpecies: "",
            petBreed: "",
            _CustomObjectKey: "",
            isActive: true,
            petProperties: [],
        };

        // push pet properties to Pet
        pet.petProperties = [];
        pet.petName = petName;
        pet.petSpecies = type;
        pet.petBirthday = `${birthdayYear}/${birthdayMonth}/01`;
        pet.petBreed =
            (((type.toLowerCase() === "cat" && chooseBySize) || (type.toLowerCase() === "dog" && !chooseBySize)) &&
                breed) ||
            "";
        pet.petProperties.push({
            ppName: "CurrentlyFeedingRC",
            ppValue: isFeedingRoyalCanin,
        } as PetPropertyModel);

        if (numberOfVists) {
            pet.petProperties.push({
                ppName: "NumberOfVisits",
                ppValue: numberOfVists,
            } as PetPropertyModel);
        }
        if (
            ((type.toLowerCase() === "cat" && !chooseBySize) || (type.toLowerCase() === "dog" && chooseBySize)) &&
            petSize
        ) {
            pet.petProperties.push({ ppName: "PetSize", ppValue: petSize } as PetPropertyModel);
        }
        if (isFeedingRoyalCanin === "1") {
            for (let i = 0; i < allProductsSelected.length; i++) {
                pet.petProperties.push({
                    ppName: "Product",
                    ppValue: allProductsSelected[i]["Name"],
                } as PetPropertyModel);
            }
        }
        if (allNeedsSelected) {
            for (const need in allNeedsSelected) {
                if (allNeedsSelected[need]) {
                    pet.petProperties.push({
                        ppName: "SpecificNeeds",
                        ppValue: allNeedsSelected[need].Name,
                    } as PetPropertyModel);
                }
            }
        }

        if (mode === "add") {
            // add new pet
            pet.isActive = true;

            addUserPetData({
                pet,
                subscriberKey,
                onSuccess: result => {
                    setIsLoading(false);
                    toaster.addToast({
                        body: <Typography>{translate("Successfully Added")}</Typography>,
                        messageType: "success",
                    });
                    // if (isPcAdmin) {
                    //     $window.location.href = impersonationPageUrl + "?subscriberKey=" + subscriberKey;
                    // } else {
                    myPetsPageUrl && history.push(myPetsPageUrl);

                    // }
                },
                onError: () => {
                    setIsLoading(false);
                    toaster.addToast({
                        body: <Typography>{translate("Failed to Add")}</Typography>,
                        messageType: "danger",
                    });
                },
            });
        } else {
            // eslint-disable-next-line no-lonely-if
            if (deletePet) {
                pet.isActive = false;
            } else {
                pet.isActive = true;
            }
            // update this pet
            pet.SubscriberID = subscriberKey;
            pet.petID = petID;
            updatePetsBySubKeyData({
                pet,
                subscriberKey,
                oldPetProperties,
                onSuccess: result => {
                    setIsLoading(false);
                    toaster.addToast({
                        body: <Typography>{translate("Successfully Updated")}</Typography>,
                        messageType: "success",
                    });
                    // if (isPcAdmin) {
                    //     $window.location.href = impersonationPageUrl + "?subscriberKey=" + subscriberKey;
                    // } else {
                    myPetsPageUrl && history.push(myPetsPageUrl);
                    // }
                },
                onError: () => {
                    setIsLoading(false);
                    toaster.addToast({
                        body: <Typography>{translate("Failed to Update")}</Typography>,
                        messageType: "danger",
                    });
                },
            });
        }

        // now submit the add/update request
    };

    const removePetInformation = (event: any) => {
        event.preventDefault();
        // show remove pet modal
        setShowRemoveConfirmModal(true);
    };

    const deletePetClickHandler = () => {
        // remove this pet, submit the api here
        prepareDataAndSubmit(true);
    };

    return (
        <StyledWrapper {...styles.container}>
            <LoadingOverlay loading={isLoading === true}>
                <form id="myPetsForm" onSubmit={handleFormSubmit} noValidate={true}>
                    <GridContainer
                        {...styles.formContainer}
                        css={css`
                            ${styles.formContainer?.css};
                            margin-top: 12px;
                        `}
                    >
                        {/* Pet Name */}
                        <GridItem {...styles.petNameGridItem}>
                            <TextField
                                label={translate("Pet Name")}
                                value={petName}
                                error={showFormErrors && !petName && customMyPetsPetNameFieldRequiredMessage}
                                onChange={petNameChangeHandler}
                                required
                                data-test-selector="petName"
                                maxLength={75}
                                type="fullName"
                                {...{ formId }}
                            />
                        </GridItem>
                        {/* Pet Birthday */}
                        <GridItem {...styles.birthDayGridItem}>
                            <GridContainer {...styles.bithdayContainer}>
                                <GridItem {...styles.dateGridItem}>
                                    <Select
                                        label={translate(" Year")}
                                        value={birthdayYear}
                                        onChange={handleBirthdayYearChange}
                                        required
                                        data-test-selector="birthdayYear"
                                        error={showFormErrors && !birthdayYear && customMyPetsYearFieldRequiredMessage}
                                    >
                                        <option value="">Select an option</option>
                                        {years.map((year: any, index) => (
                                            <option key={year.year} value={year.year}>
                                                {year.year}
                                            </option>
                                        ))}
                                    </Select>
                                </GridItem>
                                <GridItem {...styles.dateGridItem}>
                                    <Select
                                        label={translate("Month")}
                                        value={birthdayMonth}
                                        onChange={handleBirthdayMonthChange}
                                        required
                                        data-test-selector="birthdayMonth"
                                        error={
                                            showFormErrors && !birthdayMonth && customMyPetsMonthFieldRequiredMessage
                                        }
                                    >
                                        <option value="">Select an option</option>
                                        {months.map((month: any, index) => (
                                            <option key={month.value} value={month.value}>
                                                {month.display}
                                            </option>
                                        ))}
                                    </Select>
                                </GridItem>
                            </GridContainer>
                        </GridItem>
                        {/* Pet Breed */}

                        {type.toLowerCase() === "cat" ? (
                            <>
                                {!chooseBySize && (
                                    <GridItem {...styles.petHairLengthGridItem}>
                                        <Select
                                            label={translate("Hair Length (Mixed Breed)")}
                                            value={petSize}
                                            onChange={handlePetSizeChange}
                                            required={!chooseBySize}
                                            data-test-selector="petSize"
                                            error={
                                                showFormErrors && !petSize && customMyPetsPetSizeFieldRequiredMessage
                                            }
                                        >
                                            <option value="">Select an option</option>
                                            {petHairOptions.map((hair: any, index) => (
                                                <option key={hair.value} value={hair.value}>
                                                    {hair.display}
                                                </option>
                                            ))}
                                        </Select>
                                    </GridItem>
                                )}
                                <GridItem {...styles.sizeInsteadOfBreedGridItem}>
                                    <Checkbox checked={chooseBySize} onChange={chooseBySizeEventHandler}>
                                        {translate("Click here to choose by size if you can’t find your breed.")}
                                    </Checkbox>
                                </GridItem>
                                {chooseBySize && (
                                    <GridItem {...styles.breedGridItem}>
                                        <Select
                                            label={translate("Breed")}
                                            value={breed}
                                            onChange={handleBreedChange}
                                            required={chooseBySize}
                                            data-test-selector="breed"
                                            error={showFormErrors && !breed && customMyPetsPetBreedFieldRequiredMessage}
                                        >
                                            <option value="">Select an option</option>
                                            {catBreeds.map((breed: any, index) => (
                                                <option key={breed.Id} value={breed.Name}>
                                                    {breed.Name}
                                                </option>
                                            ))}
                                        </Select>
                                    </GridItem>
                                )}
                                {/* Specific Needs */}
                                <GridItem {...styles.petSpecificNeedsGridItem}>
                                    <StyledWrapper>
                                        <Select
                                            label={translate("My pet’s specific needs")}
                                            value={petNeed}
                                            onChange={handlePetNeedChange}
                                            data-test-selector="petNeeds"
                                        >
                                            <option value="">Select an option</option>
                                            {catNeeds.map((option: any, index) => (
                                                <option key={option.Id} value={option.Name}>
                                                    {option.Name}
                                                </option>
                                            ))}
                                        </Select>
                                    </StyledWrapper>
                                    {/* show selected Needs */}
                                    <StyledWrapper>
                                        {allNeedsSelected?.length > 0 &&
                                            allNeedsSelected?.map((need: MiscellaneousModel) => (
                                                <Tag key={need.Id} onDelete={() => clickRemoveNeedHandler(need.Name)}>
                                                    {need.Name}
                                                </Tag>
                                            ))}
                                    </StyledWrapper>
                                </GridItem>
                            </>
                        ) : (
                            <>
                                {!chooseBySize && (
                                    <GridItem {...styles.breedGridItem}>
                                        <Select
                                            label={translate("Breed")}
                                            value={breed}
                                            onChange={handleBreedChange}
                                            required={chooseBySize}
                                            data-test-selector="breed"
                                            error={showFormErrors && !breed && customMyPetsPetBreedFieldRequiredMessage}
                                        >
                                            <option value="">Select an option</option>
                                            {dogBreeds.map((breed: any, index) => (
                                                <option key={breed.Id} value={breed.Name}>
                                                    {breed.Name}
                                                </option>
                                            ))}
                                        </Select>
                                    </GridItem>
                                )}
                                <GridItem {...styles.sizeInsteadOfBreedGridItem}>
                                    <Checkbox checked={chooseBySize} onChange={chooseBySizeEventHandler}>
                                        {translate("Click here to choose by size if you can’t find your breed.")}
                                    </Checkbox>
                                </GridItem>
                                {chooseBySize && (
                                    <GridItem {...styles.petHairLengthGridItem}>
                                        <Select
                                            label={translate("Hair Length (Mixed Breed)")}
                                            value={petSize}
                                            onChange={handlePetSizeChange}
                                            required={!chooseBySize}
                                            data-test-selector="petSize"
                                            error={
                                                showFormErrors && !petSize && customMyPetsPetSizeFieldRequiredMessage
                                            }
                                        >
                                            <option value="">Select an option</option>
                                            {dogSizes.map((hair: any, index) => (
                                                <option key={hair.Value} value={hair.Value}>
                                                    {hair.Value}
                                                </option>
                                            ))}
                                        </Select>
                                    </GridItem>
                                )}
                                {/* Specific Needs */}
                                <GridItem {...styles.petSpecificNeedsGridItem}>
                                    <StyledWrapper>
                                        <Select
                                            label={translate("My pet’s specific needs")}
                                            value={petNeed}
                                            onChange={handlePetNeedChange}
                                            data-test-selector="petNeeds"
                                        >
                                            <option value="">Select an option</option>
                                            {dogNeeds.map((option: any, index) => (
                                                <option key={option.Id} value={option.Name}>
                                                    {option.Name}
                                                </option>
                                            ))}
                                        </Select>
                                    </StyledWrapper>
                                    {/* show selected Needs */}
                                    <StyledWrapper>
                                        {allNeedsSelected?.length > 0 &&
                                            allNeedsSelected?.map((need: MiscellaneousModel) => (
                                                <Tag key={need.Id} onDelete={() => clickRemoveNeedHandler(need.Name)}>
                                                    {need.Name}
                                                </Tag>
                                            ))}
                                    </StyledWrapper>
                                </GridItem>
                            </>
                        )}
                        {/* VET */}
                        <GridItem {...styles.petNumberOfVisitsGridItem}>
                            <Select
                                label={translate("How often do you take your pet to the vet?")}
                                value={numberOfVists}
                                onChange={handleNumberOfVistsChange}
                                data-test-selector="petNumberOfVists"
                                error={
                                    showFormErrors && !numberOfVists && customMyPetsNumberOfVisitsFieldRequiredMessage
                                }
                            >
                                <option value="">Select an option</option>
                                {petNumberOfVists.map((option: any, index) => (
                                    <option key={option.Name} value={option.Name}>
                                        {option.Name}
                                    </option>
                                ))}
                            </Select>
                        </GridItem>
                        {/* Feeding Royal Canin */}
                        <GridItem {...styles.feedingRCGridItem}>
                            <Select
                                label={translate("Are you currently feeding Royal Canin?")}
                                value={isFeedingRoyalCanin}
                                onChange={handleIsFeedingRoyalCaninChange}
                                required
                                data-test-selector="feedingRoyalCanin"
                                error={
                                    showFormErrors && !isFeedingRoyalCanin && customMyPetsFeedingFieldRequiredMessage
                                }
                            >
                                <option value="">Select an option</option>
                                {isfeedingRoyalCaninOptions.map((option: any, index) => (
                                    <option key={option.value} value={option.value}>
                                        {option.display}
                                    </option>
                                ))}
                            </Select>
                        </GridItem>
                        {/* Feeding Royal Canin */}
                        {isFeedingRoyalCanin === "1" && (
                            <GridItem {...styles.productsRCGridItem}>
                                <StyledWrapper>
                                    <DynamicDropdown
                                        label={translate("Select Partner Type")}
                                        onSelectionChange={listChangeHandler}
                                        selected={selectedProduct}
                                        options={type.toLowerCase() === "cat" ? catProductOptions : dogProductOptions}
                                        data-test-selector="petProducts"
                                    />
                                </StyledWrapper>
                                {/* show selected Needs */}
                                <StyledWrapper>
                                    {allProductsSelected?.length > 0 &&
                                        allProductsSelected?.map((option: PetProduct) => (
                                            <Tag key={option.Id} onDelete={() => clickRemoveProductHandler(option.Id)}>
                                                {option.Name}
                                            </Tag>
                                        ))}
                                </StyledWrapper>
                            </GridItem>
                        )}

                        {/* actions */}
                        <GridItem {...styles.actionsGridItem}>
                            {mode === "edit" ? (
                                <StyledWrapper
                                    css={css`
                                        display: flex;
                                        gap: 20px;
                                    `}
                                >
                                    <Button data-test-selector="MyPets_Save_Pet">
                                        {translate("Save Pet Information")}
                                    </Button>
                                    <Button
                                        type="button"
                                        onClick={event => removePetInformation(event)}
                                        data-test-selector="MyPets_Remove_Pet"
                                    >
                                        {translate("Remove Pet Information")}
                                    </Button>
                                </StyledWrapper>
                            ) : (
                                <Button type="submit" data-test-selector="MyPets_Add_Pet">
                                    {translate("Add Pet")}
                                </Button>
                            )}
                        </GridItem>
                        <TwoButtonModal
                            modalIsOpen={showRemoveConfirmModal}
                            headlineText={translate("Remove Pet")}
                            messageText={translate("Are you sure you want to remove this pit information?")}
                            cancelButtonText={translate("Cancel")}
                            submitButtonText={translate("Confirm")}
                            submitTestSelector="confirmDeletePetButton"
                            onCancel={() => setShowRemoveConfirmModal(false)}
                            onSubmit={deletePetClickHandler}
                        ></TwoButtonModal>
                    </GridContainer>
                </form>
            </LoadingOverlay>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(PetForm)));
